import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, InjectionToken, OnDestroy, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import meta from '@build-meta';
import { EMPTY, Observable, Subscription, fromEvent, interval, merge } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';

export const BUILD_DATE = new InjectionToken<number>('BUILD_DATE', {
  providedIn: 'root',
  factory: () => Number(meta.buildDate) || 0
});

export const BUILD_CHECK_INTERVAL_MS = 15 * 60 * 1000;
const RELOAD_STORAGE_KEY = 'lastReloadForBuild';
const MAX_RECORDED_BUILDS = 10;

@Injectable({
  providedIn: 'root'
})
export class CacheBusterService implements OnDestroy {
  private router = inject(Router);
  private buildDate = inject(BUILD_DATE);

  // Built on HttpBackend so the check skips the app's interceptors: no bearer token on a
  // static file, and no error toast on every navigation when the check fails.
  private readonly http: HttpClient;
  private checks?: Subscription;

  constructor() {
    const httpBackend = inject(HttpBackend);

    this.http = new HttpClient(httpBackend);
  }

  init(): void {
    this.clearInitialCache();

    this.checks?.unsubscribe();
    this.checks = merge(
      this.router.events.pipe(filter(event => event instanceof NavigationEnd)),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible')),
      interval(BUILD_CHECK_INTERVAL_MS)
    )
      .pipe(exhaustMap(() => this.checkForNewBuild()))
      .subscribe();
  }

  ngOnDestroy(): void {
    this.checks?.unsubscribe();
  }

  checkForNewBuild(): Observable<void> {
    return this.http
      .get<{ buildDate?: unknown }>(`/assets/meta.json?${Date.now()}`, { headers: { 'Cache-Control': 'no-cache' } })
      .pipe(
        map(served => this.reloadIfDifferentBuild(Number(served?.buildDate))),
        catchError(err => {
          console.warn('Build version check failed:', err);
          return EMPTY;
        })
      );
  }

  private reloadIfDifferentBuild(servedBuildDate: number): void {
    if (!Number.isFinite(servedBuildDate) || servedBuildDate <= 0 || servedBuildDate === this.buildDate) {
      return;
    }
    // A stale index.html, or two origins serving different builds, would otherwise reload forever:
    // reload at most once per build per session, and not at all when that can't be recorded.
    const reloadedBuilds = this.readReloadedBuilds();
    if (reloadedBuilds === null || reloadedBuilds.includes(servedBuildDate)) {
      return;
    }
    if (!this.writeReloadedBuilds([...reloadedBuilds, servedBuildDate].slice(-MAX_RECORDED_BUILDS))) {
      return;
    }

    const cleared =
      'caches' in window
        ? Promise.resolve()
            .then(() => caches.keys())
            .then(keys => Promise.all(keys.map(key => caches.delete(key))))
        : Promise.resolve();
    cleared.catch(err => console.warn('Cache clearing failed:', err)).finally(() => this.reloadPage());
  }

  private reloadPage(): void {
    location.reload();
  }

  private readReloadedBuilds(): number[] | null {
    try {
      return (sessionStorage.getItem(RELOAD_STORAGE_KEY) || '')
        .split(',')
        .map(Number)
        .filter(buildDate => Number.isFinite(buildDate) && buildDate > 0);
    } catch {
      return null;
    }
  }

  private writeReloadedBuilds(buildDates: number[]): boolean {
    try {
      const value = buildDates.join(',');
      sessionStorage.setItem(RELOAD_STORAGE_KEY, value);
      return sessionStorage.getItem(RELOAD_STORAGE_KEY) === value;
    } catch {
      return false;
    }
  }

  private clearInitialCache(): void {
    const isCleared = localStorage.getItem('initialCacheCleared');

    if (isCleared !== 'Yes') {
      if ('caches' in window) {
        caches.keys().then(keys => {
          Promise.all(keys.map(key => caches.delete(key)))
            .then(() => {
              localStorage.setItem('initialCacheCleared', 'Yes');
            })
            .catch(err => console.error('Initial cache clearing failed:', err));
        });
      }
    }
  }
}
